import { useEffect, useState } from 'react';
import { CATALOGS } from './catalogDescriptors';
import { listAuditEntries, type AuditEntry } from './referenceDataService';

/**
 * The reference-data audit trail (gap review 1.7): `GET /bid-reference-audit-log` shipped in P5
 * with no screen, so "who changed this rate, and from what?" needed a database session to answer.
 * ADMIN_CATALOGS-gated server-side (this page is already behind RequireBidAdmin) and stripped for
 * a blind admin — the panel renders whatever the server decided they may see.
 */
const ENTITY_OPTIONS = [
  { value: '', label: 'All entities' },
  ...CATALOGS.map((c) => ({ value: c.url.replace('/', '').replace(/-/g, '_'), label: c.label })),
  { value: 'project_change_orders', label: 'Change orders' },
  // Singular — bid_roles_service.AUDIT_ENTITY; the plural silently matched nothing (review).
  { value: 'bid_role_assignment', label: 'Role assignments' },
];

/** {rate: {old, new}} → "rate: 250 → 275" — the one-line answer the trail exists for. */
export function summariseChange(changed: Record<string, unknown> | null): string {
  if (!changed) return '';
  return Object.entries(changed)
    .map(([field, delta]) => {
      if (delta && typeof delta === 'object' && 'old' in (delta as any) && 'new' in (delta as any)) {
        const d = delta as { old: unknown; new: unknown };
        return `${field}: ${d.old ?? '—'} → ${d.new ?? '—'}`;
      }
      return field;
    })
    .join(', ');
}

const AuditPanel = (): JSX.Element => {
  const [entityType, setEntityType] = useState('');
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAuditEntries(entityType || undefined)
      .then((rows) => { if (!cancelled) setEntries(rows); })
      .catch((err) => { if (!cancelled) setError(err?.message ?? 'Could not load the audit log.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityType]);

  return (
    <div className="bid-audit-panel">
      <label className="bid-audit-filter">
        Entity
        <select value={entityType} onChange={(e) => setEntityType(e.target.value)}>
          {ENTITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>
      {loading && <p className="bid-admin-empty">Loading audit log…</p>}
      {error && <p className="bid-admin-error">{error}</p>}
      {!loading && !error && entries.length === 0 && (
        <p className="bid-admin-empty">No audit entries.</p>
      )}
      {!loading && !error && entries.length > 0 && (
        <table className="bid-table bid-audit-table">
          <thead>
            <tr><th>When</th><th>Entity</th><th>Action</th><th>Change</th><th>By</th></tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td>{String(e.performed_at).slice(0, 19)}</td>
                <td>{e.entity_type}</td>
                <td>{e.action}</td>
                <td className="bid-audit-change">{summariseChange(e.changed_fields)}{e.notes ? ` (${e.notes})` : ''}</td>
                <td className="bid-audit-by">{e.performed_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AuditPanel;
